import React, { useState, useEffect } from 'react';
import { Field } from '@xo-union/tk-component-fields';
import { FieldProps } from 'formik';
import { useDebouncedCallback } from 'use-debounce';
import moment from 'moment';

interface Props extends FieldProps {
  readOnly: boolean;
  name: string;
  label: string;
  value?: string;
  onChange: (val: string | moment.Moment) => void;
}

const DEBOUNCE_TIMEOUT = 100;

const DateDropDownField: React.FC<Props> = ({ readOnly, value, name, label, onChange, form: { errors }, ...props }) => {
  const [dateValue, setDateValue] = useState(value);
  const [debouncedOnChange] = useDebouncedCallback((momentDate: string | moment.Moment) => {
    onChange(momentDate);
  }, DEBOUNCE_TIMEOUT);

  const errorMessage = errors[name];

  const isValidDate =
    !errorMessage && (!dateValue || moment.utc(dateValue, ['MM/DD/YYYY', 'MMMM DD, YYYY'], true).isValid());

  useEffect(() => {
    if (value) {
      setDateValue(value);
    }
  }, [value]);

  return (
    <Field
      readOnly={readOnly}
      name={name}
      label={label}
      value={dateValue}
      onChange={(evt: React.FormEvent<HTMLInputElement>): void => {
        setDateValue(evt.currentTarget.value);
        const momentDate = moment.utc(evt.currentTarget.value, ['MM/DD/YYYY', 'MMMM DD, YYYY'], true);
        if (momentDate.isValid()) {
          debouncedOnChange(momentDate);
        } else {
          debouncedOnChange(evt.currentTarget.value);
        }
      }}
      state={isValidDate ? 'neutral' : 'invalid'}
      subText={
        !isValidDate && !readOnly ? errorMessage || 'Please enter a valid date in the correct format (MM/DD/YYYY)' : ''
      }
      placeholder={readOnly ? ' ' : 'MM/DD/YYYY'}
      {...props}
      data-testid="date-picker-dropdown"
    />
  );
};

export default DateDropDownField;
